import React, { useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  useTheme,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMutation } from "@apollo/client";
import {
  GENERATE_TRUTO_LINK_TOKEN,
  STORE_INTEGRATION_RESOURCE_DETAILS,
} from "../../../../apis/settingsApis/integration-apis/integrationApis";
import authenticate from "@truto/truto-link-sdk";
import InformationStepsCard from "../../../../components/InformationStepsCard/InformationStepsCard";
import { pollConnectHealth } from "../utils/integrationUtils";

const TrutoIntegrationConnectionDrawer = ({
  open,
  onClose,
  integrationKey,
  integrationLabel,
  integrationLogo,
  refetchIntegrationDetails,
  onError,
  config = {},
}) => {
  const theme = useTheme();
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const [generateTrutoLinkToken, { loading: generatingToken }] = useMutation(
    GENERATE_TRUTO_LINK_TOKEN
  );

  const [storeIntegrationDetails, { loading: storingDetails }] = useMutation(
    STORE_INTEGRATION_RESOURCE_DETAILS
  );

  const {
    heading = `Connect ${integrationLabel}`,
    subtitle = `Follow the steps below to establish a secure connection with ${integrationLabel}`,
    steps = [],
    footerText,
    alert,
    confirmation,
    beforeConnecting = [],
    customContent,
  } = config;

  const handleConnect = async () => {
    if (confirmation && !hasConfirmed) {
      return;
    }

    try {
      const { data } = await generateTrutoLinkToken({
        variables: {},
      });

      if (!generatingToken && data?.generateTrutoLinkToken?.linkToken) {
        const linkToken = data.generateTrutoLinkToken.linkToken;

        const response = await authenticate(linkToken, {
          noBack: true,
          skipRapidForm: false,
          iframe: true,
          integrations: integrationKey ? [integrationKey] : [],
        });

        if (response.result === "success") {
          const integratedAccountId = response.integrated_account_id;
          const finalIntegrationKey = response.integration || integrationKey;

          let storeResult;
          try {
            storeResult = await storeIntegrationDetails({
              variables: {
                integratedAccountId,
                integrationKey: finalIntegrationKey,
                integrationType: "TRUTO",
                sync: true,
              },
            });
          } catch (storeError) {
            console.error("Error storing integration details:", storeError);
            if (onError) {
              onError(
                "Connection successful but failed to save integration details. Please refresh the page."
              );
            }
            return;
          }

          const payload = storeResult?.data?.storeIntegrationResourceId;
          if (payload && payload.success === false) {
            if (onError) {
              onError(payload.message || "Failed to connect integration.");
            }
            return;
          }

          // Poll for the first ~10s to catch a broken-at-connect state (bad
          // scopes, revoked token, plan gate). Truto's first sync run usually
          // reports the error within that window; the collector flips
          // is_active=false and stores the reason on inactivity_reason.
          if (refetchIntegrationDetails) {
            const health = await pollConnectHealth(
              refetchIntegrationDetails,
              integratedAccountId
            );
            if (health.status === "failed") {
              if (onError) {
                onError(
                  health.inactivityReason
                    ? `Connection failed: ${health.inactivityReason}`
                    : "Connection failed. Please reconnect."
                );
              }
              onClose();
              return;
            }
          }

          onClose();
        } else {
          if (onError) {
            onError("Failed to connect integration. Please try again.");
          }
        }
      }
    } catch (error) {
      console.error("Error during Truto Link connection:", error);
      if (onError) {
        onError("An error occurred during connection. Please try again.");
      }
    }
  };

  const handleClose = () => {
    setHasConfirmed(false);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      TransitionComponent={useMemo(
        () => (props) => <Slide {...props} direction="left" />,
        []
      )}
      PaperProps={{
        sx: {
          width: "75%",
          maxWidth: "1400px",
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: theme.customColors.background.cardHeader,
                border: `1px solid ${theme.customColors.border.main}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {integrationLogo ? (
                <img
                  src={`${process.env.REACT_APP_POLICY_DOC_URL}/logos/${integrationLogo}`}
                  alt={integrationLabel}
                  style={{ width: 32, height: 32, objectFit: "contain" }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: theme.customColors.text.secondary,
                  }}
                >
                  {integrationLabel?.charAt(0) || "?"}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  letterSpacing: "-0.02em",
                }}
              >
                Connect {integrationLabel}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  mt: 0.5,
                }}
              >
                Follow the steps below to establish a secure connection
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.customColors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2.5,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.customColors.border.main,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.customColors.border.strong,
          },
        }}
      >
        {customContent}

        {alert && (
          <Box sx={{ mb: 2.5 }}>
            <Alert
              severity={alert.severity || "info"}
              sx={{
                mb: beforeConnecting.length > 0 ? 2 : 0,
                backgroundColor:
                  theme.customColors.status[alert.severity || "info"]?.bg ||
                  theme.customColors.status.info.bg,
                border: `1px solid ${theme.customColors.status[alert.severity || "info"]?.main ||
                  theme.customColors.status.info.main
                  }`,
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color:
                    theme.customColors.status[alert.severity || "info"]?.text ||
                    theme.customColors.status.info.text,
                  mb: alert.message ? 0.75 : 0,
                }}
              >
                {alert.title}
              </Typography>
              {alert.message && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.strong, mt: 0.5 }}
                >
                  {alert.message}
                </Typography>
              )}
            </Alert>

            {/* Before Connecting List */}
            {beforeConnecting.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: "8px",
                  backgroundColor: theme.customColors.background.cardHeader,
                  border: `1px solid ${theme.customColors.border.main}`,
                  mt: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.strong,
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  Before connecting:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
                  {beforeConnecting.map((item, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{
                        color: theme.customColors.text.secondary,
                        mb: index < beforeConnecting.length - 1 ? 0.75 : 0,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Steps Card */}
        {steps.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <InformationStepsCard
              heading={heading}
              subtitle={subtitle}
              steps={steps}
              footerText={footerText}
              collapsible={true}
              defaultExpanded={true}
            />
          </Box>
        )}

        {/* Confirmation Checkbox */}
        {confirmation && (
          <Box
            sx={{
              p: 2.5,
              borderRadius: "8px",
              backgroundColor: theme.customColors.background.cardHeader,
              border: `1px solid ${theme.customColors.border.main}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <input
              type="checkbox"
              checked={hasConfirmed}
              onChange={(e) => setHasConfirmed(e.target.checked)}
              style={{
                width: "20px",
                height: "20px",
                cursor: "pointer",
                marginTop: "2px",
              }}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.strong,
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                {confirmation.label}
              </Typography>
              {confirmation.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    fontSize: "0.8125rem",
                  }}
                >
                  {confirmation.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#FFFFFF",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            color: theme.customColors.text.primary,
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConnect}
          disabled={
            (confirmation && !hasConfirmed) || generatingToken || storingDetails
          }
          startIcon={<CheckCircleIcon />}
          sx={{
            backgroundColor: "#031B89",
            color: "white",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#0056b3",
            },
            "&:disabled": {
              backgroundColor: theme.customColors.background.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {generatingToken || storingDetails
            ? "Connecting..."
            : "Connect Integration"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default TrutoIntegrationConnectionDrawer;
